import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import axios from "axios";
import getDistance from "geolib/es/getDistance";

// Module-level cache — fetched once per page load
let cachedEvents = null;
let fetchPromise = null;

async function fetchPublicEvents() {
  if (cachedEvents) return cachedEvents;
  if (fetchPromise) return fetchPromise;

  fetchPromise = axios
    .get("/api/events/public")
    .then((res) => {
      cachedEvents = res.data;
      return cachedEvents;
    })
    .catch(() => {
      fetchPromise = null;
      return [];
    });

  return fetchPromise;
}

const SESSION_KEY_PREFIX = "vegifyit_event_toast_shown_";

/**
 * Hook that checks for active events near the current map center.
 * Returns the closest qualifying event (or null) and a dismiss function.
 */
const useNearbyEvents = () => {
  const [nearbyEvent, setNearbyEvent] = useState(null);
  const [dismissed, setDismissed] = useState({});
  const location = useLocation();
  const mapCenter = useSelector((state) => state.mapCenter);
  const radius = useSelector((state) => state.currentLocation.radius);
  const eventsRef = useRef([]);

  // Load events once
  useEffect(function loadPublicEventsOnMount() {
    fetchPublicEvents().then((events) => {
      eventsRef.current = events;
    });
  }, []);

  // Check proximity whenever mapCenter changes
  useEffect(function checkProximityWhenMapCenterChanges() {
    // Skip if on an event route
    if (/^\/events\//.test(location.pathname)) {
      setNearbyEvent(null);
      return;
    }

    if (!mapCenter?.lat || !mapCenter?.lng) return;

    const events = eventsRef.current;
    if (!events || events.length === 0) return;

    const threshold = Math.max(20, Math.min(radius || 50, 100)) * 1000; // convert km to meters

    let closest = null;
    let closestDist = Infinity;

    for (const event of events) {
      if (!event.lat || !event.lng) continue;

      // Skip if already dismissed this session
      const key = SESSION_KEY_PREFIX + event._id;
      if (dismissed[event._id] || sessionStorage.getItem(key)) continue;

      const dist = getDistance(
        { latitude: mapCenter.lat, longitude: mapCenter.lng },
        { latitude: event.lat, longitude: event.lng }
      );

      if (dist <= threshold && dist < closestDist) {
        closest = event;
        closestDist = dist;
      }
    }

    setNearbyEvent(closest);
  }, [mapCenter, radius, location.pathname, dismissed]);

  const dismiss = useCallback((eventId) => {
    sessionStorage.setItem(SESSION_KEY_PREFIX + eventId, "1");
    setDismissed((prev) => ({ ...prev, [eventId]: true }));
    setNearbyEvent(null);
  }, []);

  return { nearbyEvent, dismiss };
};

export default useNearbyEvents;
